import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import baseEnv from 'config/config';

export type AmplifySessionTokens = {
  idToken: string;
  accessToken: string;
  refreshToken: string;
};

/**
 * Install minted Cognito tokens into the Amplify local session so
 * Auth.currentSession() becomes the player. Call only after Auth.signOut()
 * has cleared the staff session.
 */
export function installAmplifySession(tokens: AmplifySessionTokens): string {
  const authConfig = baseEnv().amplify;
  const userPool = new CognitoUserPool({
    UserPoolId: authConfig.userPoolId,
    ClientId: authConfig.userPoolWebClientId,
  });

  const idToken = new CognitoIdToken({ IdToken: tokens.idToken });
  const accessToken = new CognitoAccessToken({ AccessToken: tokens.accessToken });
  const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refreshToken });
  const session = new CognitoUserSession({
    IdToken: idToken,
    AccessToken: accessToken,
    RefreshToken: refreshToken,
  });

  const payload = accessToken.decodePayload() as { username?: string; sub?: string };
  const idPayload = idToken.decodePayload() as { 'cognito:username'?: string; sub?: string };
  const username =
    payload.username || idPayload['cognito:username'] || idPayload.sub || payload.sub;
  if (!username) {
    throw new Error('Minted tokens missing Cognito username');
  }

  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });
  cognitoUser.setSignInUserSession(session);
  return username;
}

export function groupsFromAccessToken(accessTokenJwt: string): string[] {
  try {
    const payload = JSON.parse(atob(accessTokenJwt.split('.')[1])) as {
      'cognito:groups'?: string[];
    };
    return payload['cognito:groups'] || [];
  } catch {
    return [];
  }
}
